import axios from 'axios'
import qs from 'qs'
import { cloneDeep } from 'lodash'

import config from '../../config'
import { getHeaders, embedsToQuery } from '../../lib/api'
import { DEFAULT_EMBEDS as TASK_EMBEDS } from '../TaskAPIProvider'
import { getNoteOrder, setNoteOrder } from '@SonarCX/lib/idb'
import { uniqueArray } from '@SonarCX/utilities'
import { sortByOrder } from '@SonarCX/utilities/tsUtils'

// Used to map keys to api routes
export const KEY_MAP = {
  conflict: params => `/users/${params[1]}/channels/conflicts`,
  assignedTasks: params =>
    `/tasks${embedsToQuery(TASK_EMBEDS)}&assigned_to[]=${params[1]}${
      params[2]
    }`,
  assignedToOthersTasks: params =>
    `/tasks${embedsToQuery(TASK_EMBEDS)}&owner_id=${
      params[1]
    }&assigned_to_other=${params[1]}&include_unassigned=false${params[2]}`,
  ownTasks: params =>
    `/tasks${embedsToQuery(TASK_EMBEDS)}&owner_id=${
      params[1]
    }&assigned_to_only=${params[1]}&include_unassigned=true${params[2]}`,
  allTasks: params => `/tasks?${params[1]}`,
  category: params => `/tasks${embedsToQuery(TASK_EMBEDS)}&${params[1]}`,
  taskStats: params => `/users/${params[1]}/stats/tasks`,
  chatStats: params => `/users/${params[1]}/stats/conversations`,
  taskDashboard: params =>
    params.length === 2
      ? `/users/${params[1]}/tasks?limit=25`
      : `/users/${params[1]}/tasks?limit=25&after=${params[2]}`,
  userSearch: params => `/users${params[1]}`,
  taskDashboard2: params => {
    const sort = encodeURIComponent('+due')
    const base = `/tasks?per_page=25&sort=${sort}&embed[]=sub_tasks&include_subtasks=true`

    let suffix = ''

    if (params[2] !== 'null') {
      suffix += `&assigned_to[]=${params[2]}`
    }

    // Decode the date range
    const [startDate, endDate] = params[3]
      .split('--')
      .map(s => s.replaceAll('..', ':'))

    suffix += `&due_start=${startDate}&due_end=${endDate}`

    return `${base}&page=${params[1]}${suffix}`
  },
  reminderDashboard: params => {
    const sort = encodeURIComponent('+stamp')
    const base = `/reminders?${params[2]}&per_page=25`
    return `${base}&page=${params[1]}&sort[]=${sort}`
  },
  planner: params =>
    `/tasks?${params[1]}&assigned_to[]=${params[2]}&per_page=100`,
  userTasks: params => `/tasks${[params[1]]}`,
  subtasks: params =>
    params.length === 2
      ? `/tasks/${params[1]}/subtasks`
      : `/tasks/${params[1]}/subtasks?${params[2]}`,
  agentNotes: params => `/notes/pages`
}

// Convenience method to map data from api
const getFullData = (data, params, fullData) => fullData

// Used to process the returned data, based off the key
const DATA_TRANSFORM_MAP = {
  conflict: (data, params) => ({ data, session: params[2] }),
  assignedTasks: getFullData,
  assignedToOthersTasks: getFullData,
  ownTasks: getFullData,
  allTasks: getFullData,
  category: getFullData,
  taskDashboard: getFullData,
  taskDashboard2: getFullData,
  userTasks: getFullData,
  subtasks: getFullData,
  reminderDashboard: getFullData,
  agentNotes: async (data, params, fullData) => {
    const envName = params[1]
    const userID = params[2]
    const ids: string[] = data.map(note => note.id)

    // Get the note order
    const order = await getNoteOrder({ envName, userID })
    const merged: string[] = uniqueArray(order, ids).filter(Boolean)

    // Update data order
    const sorted = sortByOrder(data, merged, 'id')

    // Save order
    await setNoteOrder({ envName, userID, items: merged })

    return sorted
  }
}

// Takes a key and turns it into an api route
const mapKeyToAPIRoute = key => {
  // Key should be of the format 'key:param1:param2:...:paramN'
  // where key is a key in the above dictionaries and params are any extra parameters of those functions
  const params = key.split(':')

  // Warn but allow unmapped keys
  if (params.length === 0 || !(params[0] in KEY_MAP)) {
    console.warn(`SWR resource not mapped: ${key}`)
    return key
  }

  // Get the key transform function
  const transformer = KEY_MAP[params[0]]

  // Transformer could be a function
  if (typeof transformer === 'function') {
    return transformer(params)
  }

  // String case
  return transformer
}

// Processes the data from the api
const transformAPIData = (key, data, fullData) => {
  // Same format as above
  const params = key.split(':')

  // Return un-transformed data when no transformer is specified
  if (params.length === 0 || !(params[0] in DATA_TRANSFORM_MAP)) {
    return data
  }

  // Transform and return the data
  const transformer = DATA_TRANSFORM_MAP[params[0]]
  const transformed = transformer(data, params, fullData)
  return transformed
}

// Performs a fetch when the key is a url with few params
const performStringFetch = async key => {
  const uri = `${config.apiBaseUri}${key}`
  const result = await axios.get(uri, await getHeaders())
  return result
}

const fixTaskParams = params => {
  const NUMBER_KEYS = ['page', 'per_page', 'due_within']
  const DATE_KEYS = ['due_start', 'due_end']

  const fixed = cloneDeep(params)

  // Convert params to correct types
  Object.entries(fixed).forEach(([key, value]) => {
    if (NUMBER_KEYS.includes(key)) {
      // @ts-expect-error
      fixed[key] = parseInt(value)
      return
    }

    if (DATE_KEYS.includes(key)) {
      // @ts-expect-error
      fixed[key] = new Date(value)
      return
    }

    if (key !== 'filter_custom') {
      return
    }

    const { field_id, logic, value: values } = value[0]

    // If not an array, just assign as an array
    if (!Array.isArray(field_id)) {
      fixed[key] = [value[0]]
      return
    }

    // Need to unwrap the filter_custom key
    const filters = []
    for (let i = 0; i < field_id.length; i++) {
      filters.push({
        field_id: field_id[i],
        logic: logic[i],
        value: values[i]
      })
    }
    fixed[key] = filters
  })

  return fixed
}

// Performs a fetch when the key is an object with url and many params
const performTaskPostFetch = async key => {
  const [url, rest] = key.split('?')
  const parsedParams = qs.parse(rest)
  const fixedParams = fixTaskParams(parsedParams)

  const uri = `${config.apiBaseUri}${url}`
  const baseHeaders = await getHeaders()
  const headers = {
    headers: {
      ...baseHeaders.headers,
      'X-HTTP-Method-Override': 'GET'
    }
  }
  const result = await axios.post(uri, fixedParams, headers)
  return result
}

// Does the fetching of data based on the given key
const fetcher = async key => {
  const mappedKey = mapKeyToAPIRoute(key) ?? ''

  let result = null
  if (mappedKey.startsWith('/tasks?')) {
    result = await performTaskPostFetch(mappedKey)
  } else {
    result = await performStringFetch(mappedKey)
  }

  const data = result?.data?.data
  return transformAPIData(key, data, result.data)
}

export default fetcher
